import React, {createRef} from "react";
import {createRoot} from "react-dom/client";
import {ThemeProvider} from "@mui/material/styles";
import theme from "../../../../theme";
import {AgenticSectionControls} from "./agenticSection/AgenticSectionControls";
import {
    deleteCurrentNode,
    focusPromptInput,
    selectCurrentNode,
} from "./agenticSection/agenticSectionNodeViewPm";
import {isInteractiveTarget} from "./nodeViewDomHelpers";
import {
    ensureSectionId,
    getCurrentSectionState,
    queueGenerationForNode,
    removeSectionStateForNode,
    submitFeedbackForNode,
    subscribeToSectionFeedback,
    updateDraftForNode,
} from "./agenticSection/agenticSectionNodeViewStore";
import {
    canSubmitAgenticSection,
    getAgenticSectionViewState,
} from "./agenticSection/agenticSectionState";

const AGENTIC_SECTION_DEBUG = process.env.NODE_ENV === "development";
let agenticSectionInstanceSequence = 0;

function shallowEqualAttrs(left = {}, right = {}) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    for (const key of leftKeys) {
        if (left[key] !== right[key]) return false;
    }
    return true;
}

function buildFeedbackSignature(entries = []) {
    return entries
        .map((entry) => [
            entry?.id || "",
            entry?.requestId || "",
            entry?.direction || "",
            entry?.comment || "",
            entry?.userId || "",
            entry?.createdAtMillis || 0,
        ].join("::"))
        .join("|");
}

function buildCurrentUserOption(currentUser) {
    if (!currentUser?.uid || currentUser.uid === "ai") return null;

    const label = currentUser.displayName || currentUser.email || currentUser.uid;
    return {
        id: currentUser.uid,
        label,
        name: label,
        email: currentUser.email || "",
    };
}

function summarizeText(value = "") {
    if (typeof value !== "string") return "";
    return value.trim().replace(/\s+/g, " ").slice(0, 80);
}

function summarizeResponseObject(value = "") {
    if (!value) return {hasResponseObject: false, responseObjectLength: 0};

    const normalizedValue = typeof value === "string"
        ? value
        : JSON.stringify(value);

    return {
        hasResponseObject: Boolean(normalizedValue),
        responseObjectLength: normalizedValue?.length || 0,
    };
}

function diffAttrKeys(left = {}, right = {}) {
    const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
    return Array.from(keys).filter((key) => left?.[key] !== right?.[key]).sort();
}

function getSelectionSummary(selection) {
    if (!selection) return null;

    return {
        type: selection.constructor?.name || "UnknownSelection",
        from: typeof selection.from === "number" ? selection.from : null,
        to: typeof selection.to === "number" ? selection.to : null,
        empty: Boolean(selection.empty),
        nodeType: selection.node?.type?.name || null,
    };
}

function buildRenderSignature(viewState, feedbackEntries = [], uiState = {}) {
    return JSON.stringify({
        promptText: viewState.promptText,
        selectedAgent: viewState.selectedAgent,
        requestId: viewState.requestId,
        responseText: viewState.responseText,
        responseObject: viewState.responseObject,
        responseStatus: viewState.responseStatus,
        responseUpdatedAt: viewState.responseUpdatedAt,
        errorMessage: viewState.errorMessage,
        lastSubmittedPromptText: viewState.lastSubmittedPromptText,
        lastSubmittedAgent: viewState.lastSubmittedAgent,
        createdAt: viewState.createdAt,
        updatedAt: viewState.updatedAt,
        canSubmit: viewState.canSubmit,
        isSubmitting: viewState.isSubmitting,
        feedbackSignature: buildFeedbackSignature(feedbackEntries),
        uiRevision: uiState.uiRevision || 0,
        feedbackDialogOpen: Boolean(uiState.feedbackDialogOpen),
        feedbackDirection: uiState.feedbackDirection || "up",
        feedbackComment: uiState.feedbackComment || "",
        activeCitationSignature: JSON.stringify(uiState.activeCitationGroup || null),
    });
}

export class AgenticSectionNodeView {
    constructor(node, view, getPos, options = {}) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.currentUser = options.currentUser || null;
        this.instanceId = ++agenticSectionInstanceSequence;

        this.dom = document.createElement("div");
        this.dom.className = "pm-agentic-section";
        this.dom.setAttribute("contenteditable", "false");

        this.reactContainer = document.createElement("div");
        this.reactContainer.className = "pm-agentic-section__root";
        this.dom.appendChild(this.reactContainer);

        this.promptInputRef = createRef();
        this.feedbackEntries = [];
        this.feedbackSectionId = null;
        this.unsubscribeFeedback = () => {
        };
        this.isDestroyed = false;
        this.lastRenderSignature = null;
        this.lastRenderReason = null;
        this.uiRevision = 0;

        this.feedbackDialogOpen = false;
        this.feedbackDirection = "up";
        this.feedbackComment = "";
        this.activeCitationGroup = null;

        this.root = createRoot(this.reactContainer);
        this.debugLog("constructor:start", {attrs: this.buildAttrSummary(this.node.attrs)});
        ensureSectionId(this);
        this.ensureDefaultSelectedAgent();
        this.syncFeedbackSubscription();
        this.render("constructor");
    }

    getCurrentUserOption() {
        return buildCurrentUserOption(this.currentUser);
    }

    bumpUiRevision() {
        this.uiRevision += 1;
    }

    requestRender(reason = "requestRender") {
        if (this.isDestroyed) return false;
        this.bumpUiRevision();
        return this.render(reason);
    }

    safeGetPos() {
        if (typeof this.getPos !== "function") return null;

        try {
            const pos = this.getPos();
            return typeof pos === "number" ? pos : null;
        } catch (_error) {
            return null;
        }
    }

    buildAttrSummary(attrs = {}) {
        return {
            sectionId: attrs.sectionId || null,
            selectedAgent: attrs.selectedAgent || "",
            requestId: attrs.requestId || null,
            responseStatus: attrs.responseStatus || "idle",
            promptPreview: summarizeText(attrs.promptText),
            promptLength: attrs.promptText?.length || 0,
            responseTextLength: attrs.responseText?.length || 0,
            createdAt: attrs.createdAt || null,
            updatedAt: attrs.updatedAt || null,
            ...summarizeResponseObject(attrs.responseObject),
        };
    }

    buildDebugMeta(extra = {}) {
        return {
            instanceId: this.instanceId,
            pos: this.safeGetPos(),
            sectionId: this.node?.attrs?.sectionId || this.feedbackSectionId || null,
            selection: getSelectionSummary(this.view?.state?.selection),
            ...extra,
        };
    }

    debugLog(event, extra = {}) {
        if (!AGENTIC_SECTION_DEBUG || this.isDestroyed) return;
        console.log("[agenticSectionNodeView]", this.buildDebugMeta({event, ...extra}));
    }

    ensureDefaultSelectedAgent() {
        const currentSectionState = this.getCurrentSectionState();
        const currentUserId = this.getCurrentUserOption()?.id || "";
        if (!currentSectionState.selectedAgent && currentUserId) {
            this.debugLog("ensureDefaultSelectedAgent:dispatch", {selectedAgent: currentUserId});
            updateDraftForNode(this, {selectedAgent: currentUserId});
            return true;
        }
        return false;
    }

    syncFeedbackSubscription() {
        const sectionId = ensureSectionId(this);
        if (!sectionId || sectionId === this.feedbackSectionId) return;

        if (typeof this.unsubscribeFeedback === "function") {
            this.debugLog("feedback:unsubscribe", {previousSectionId: this.feedbackSectionId});
            this.unsubscribeFeedback();
        }
        this.feedbackSectionId = sectionId;
        this.debugLog("feedback:subscribe", {feedbackSectionId: sectionId});
        this.unsubscribeFeedback = subscribeToSectionFeedback(
            sectionId,
            (feedbackEntries) => {
                if (this.isDestroyed) return;
                this.feedbackEntries = feedbackEntries;
                this.debugLog("feedback:onChange", {
                    feedbackCount: feedbackEntries.length,
                    visibleFeedbackCount: this.getCurrentSectionState().requestId
                        ? feedbackEntries.filter((entry) => entry?.requestId === this.getCurrentSectionState().requestId).length
                        : 0,
                });
                this.render("feedback:onChange");
            },
            (error) => {
                console.error("[AgenticSectionNodeView] feedback subscription error", error);
            }
        );
    }

    dispatchNodeAttrs(partialAttrs, addToHistory = false) {
        if (typeof this.getPos !== "function") return;

        const pos = this.safeGetPos();
        if (typeof pos !== "number") return;

        const currentNode = this.view.state.doc.nodeAt(pos);
        if (!currentNode || currentNode.type !== this.node.type) return;

        const nextAttrs = {
            ...currentNode.attrs,
            ...partialAttrs,
        };
        if (shallowEqualAttrs(currentNode.attrs, nextAttrs)) return;

        this.debugLog("dispatchNodeAttrs", {
            addToHistory,
            changedKeys: diffAttrKeys(currentNode.attrs, nextAttrs),
            nextAttrs: this.buildAttrSummary(nextAttrs),
        });

        const tr = this.view.state.tr.setNodeMarkup(pos, null, nextAttrs);
        tr.setMeta("addToHistory", addToHistory);
        this.view.dispatch(tr);
    }

    getCurrentSectionState() {
        return getCurrentSectionState(this);
    }

    handleDelete = () => {
        removeSectionStateForNode(this);
        deleteCurrentNode(this);
    };

    handleSubmit = (promptText, selectedAgent) => {
        const currentSectionState = this.getCurrentSectionState();
        if (!canSubmitAgenticSection(currentSectionState, {promptText, selectedAgent})) return;

        queueGenerationForNode(this, {promptText, selectedAgent});
        this.view.focus();
    };

    handleFeedbackSubmit = async () => {
        await submitFeedbackForNode(this, {
            direction: this.feedbackDirection,
            comment: this.feedbackComment,
        });
        if (!this.isDestroyed) {
            this.closeFeedbackDialog();
        }
    };

    handlePromptTextChange = (promptText) => {
        updateDraftForNode(this, {promptText});
    };

    handleInputKeyDown = (event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        event.stopPropagation();
        selectCurrentNode(this);
    };

    openFeedbackDialog = (direction = "up") => {
        this.feedbackDirection = direction === "down" ? "down" : "up";
        this.feedbackComment = "";
        this.feedbackDialogOpen = true;
        this.requestRender("openFeedbackDialog");
    };

    closeFeedbackDialog = () => {
        if (!this.feedbackDialogOpen && !this.feedbackComment) return;
        this.feedbackDialogOpen = false;
        this.feedbackComment = "";
        this.requestRender("closeFeedbackDialog");
    };

    handleFeedbackCommentChange = (comment) => {
        const nextComment = typeof comment === "string" ? comment : "";
        if (nextComment === this.feedbackComment) return;
        this.feedbackComment = nextComment;
        this.requestRender("handleFeedbackCommentChange");
    };

    openCitationGroup = (citationGroup) => {
        this.activeCitationGroup = citationGroup || null;
        this.requestRender("openCitationGroup");
    };

    closeCitationGroup = () => {
        if (!this.activeCitationGroup) return;
        this.activeCitationGroup = null;
        this.requestRender("closeCitationGroup");
    };

    render(reason = "render") {
        const viewState = getAgenticSectionViewState(this.getCurrentSectionState());
        const effectiveSelectedAgent = viewState.selectedAgent || this.getCurrentUserOption()?.id || "";
        const visibleFeedbackEntries = viewState.requestId
            ? this.feedbackEntries.filter((entry) => entry?.requestId === viewState.requestId)
            : [];
        const renderSignature = buildRenderSignature({...viewState, selectedAgent: effectiveSelectedAgent}, visibleFeedbackEntries, {
            uiRevision: this.uiRevision,
            feedbackDialogOpen: this.feedbackDialogOpen,
            feedbackDirection: this.feedbackDirection,
            feedbackComment: this.feedbackComment,
            activeCitationGroup: this.activeCitationGroup,
        });

        if (renderSignature === this.lastRenderSignature) {
            this.debugLog("render:skip", {
                reason,
                previousReason: this.lastRenderReason,
                viewState: this.buildAttrSummary({...viewState, selectedAgent: effectiveSelectedAgent}),
                uiRevision: this.uiRevision,
                feedbackCount: this.feedbackEntries.length,
                visibleFeedbackCount: visibleFeedbackEntries.length,
            });
            return false;
        }

        this.lastRenderSignature = renderSignature;
        this.lastRenderReason = reason;

        this.debugLog("render", {
            reason,
            viewState: this.buildAttrSummary({...viewState, selectedAgent: effectiveSelectedAgent}),
            uiRevision: this.uiRevision,
            feedbackCount: this.feedbackEntries.length,
            visibleFeedbackCount: visibleFeedbackEntries.length,
        });

        this.root.render(
            <ThemeProvider theme={theme}>
                <AgenticSectionControls
                    promptText={viewState.promptText}
                    selectedAgent={effectiveSelectedAgent}
                    responseStatus={viewState.responseStatus}
                    responseText={viewState.responseText}
                    responseData={viewState.responseData}
                    errorMessage={viewState.errorMessage}
                    feedbackEntries={visibleFeedbackEntries}
                    canSubmit={viewState.canSubmit}
                    isSubmitting={viewState.isSubmitting}
                    onPromptTextChange={this.handlePromptTextChange}
                    onSubmit={this.handleSubmit}
                    inputRef={this.promptInputRef}
                    onInputKeyDown={this.handleInputKeyDown}
                    feedbackDialogOpen={this.feedbackDialogOpen}
                    feedbackDirection={this.feedbackDirection}
                    feedbackComment={this.feedbackComment}
                    onOpenFeedback={this.openFeedbackDialog}
                    onCloseFeedbackDialog={this.closeFeedbackDialog}
                    onFeedbackCommentChange={this.handleFeedbackCommentChange}
                    onSubmitFeedback={this.handleFeedbackSubmit}
                    activeCitationGroup={this.activeCitationGroup}
                    onCitationGroupClick={this.openCitationGroup}
                    onCloseCitationGroup={this.closeCitationGroup}
                />
            </ThemeProvider>
        );

        return true;
    }

    focusInput() {
        return focusPromptInput(this.promptInputRef);
    }

    update(node) {
        if (node.type !== this.node.type) return false;

        const attrsChanged = !shallowEqualAttrs(this.node.attrs, node.attrs);
        const changedKeys = attrsChanged ? diffAttrKeys(this.node.attrs, node.attrs) : [];

        this.debugLog("update", {
            attrsChanged,
            changedKeys,
            previousAttrs: attrsChanged ? this.buildAttrSummary(this.node.attrs) : undefined,
            nextAttrs: attrsChanged ? this.buildAttrSummary(node.attrs) : undefined,
        });

        this.node = node;
        if (!this.node.attrs.sectionId) ensureSectionId(this);
        this.ensureDefaultSelectedAgent();
        this.syncFeedbackSubscription();
        if (attrsChanged) {
            this.render("update:attrsChanged");
        }
        return true;
    }

    selectNode() {
        this.dom.classList.add("ProseMirror-selectednode");
        this.debugLog("selectNode");
    }

    deselectNode() {
        this.dom.classList.remove("ProseMirror-selectednode");
        this.debugLog("deselectNode");
    }

    stopEvent(event) {
        const target = event?.target;
        if (isInteractiveTarget(target)) {
            this.debugLog("stopEvent", {result: true, targetTag: target?.nodeName || null, reason: "interactiveTarget"});
            return true;
        }

        if (target instanceof Node && this.reactContainer.contains(target)) {
            this.debugLog("stopEvent", {result: true, targetTag: target?.nodeName || null, reason: "insideReactContainer"});
            return true;
        }

        this.debugLog("stopEvent", {result: false, targetTag: target?.nodeName || null, reason: "allowProseMirror"});
        return false;
    }

    ignoreMutation() {
        return true;
    }

    destroy() {
        this.debugLog("destroy", {
            lastRenderReason: this.lastRenderReason,
            feedbackCount: this.feedbackEntries.length,
        });
        this.isDestroyed = true;
        if (typeof this.unsubscribeFeedback === "function") {
            this.unsubscribeFeedback();
        }
        this.root.unmount();
    }
}
